import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { PiArrowLineDown, PiCornersOut, PiCrosshairSimple, PiGridFour, PiImage } from 'react-icons/pi';
import {
  TOOLBAR_DENSITY_REGULAR,
  TOOLBAR_TONE_SURFACE,
  TOOLBAR_VARIANT_FLOATING,
  TOOLTIP_PLACEMENT_TOP,
  Tooltip,
  Toolbar,
} from '@aibuild/ui';
import type { Environment, EnvironmentToggle } from '../viewerSettings';
import { BACKDROP_TYPES } from '../viewport';
import type { UnderLightSettings } from '../viewport';
import { usePortalContainer } from '../environment';
import ChromeButton from './ChromeButton';
import { useChromeLayout } from './ChromeContext';
import { AmbientOcclusionIcon, PlatformIcon, ShadowIcon, UnderLightIcon } from './icons';
import { messages } from './messages';
import UnderLightPanel from './UnderLightPanel';
import { usePopover } from './usePopover';
import { BottomSlot, HiddenFileInput, LightGroup, Notice, Swatch } from './EnvironmentBar.styled';

const TOGGLES: readonly { key: EnvironmentToggle; icon: React.ReactNode; label: keyof typeof messages }[] = [
  { key: 'centre', icon: <PiCrosshairSimple />, label: 'centre' },
  { key: 'sit', icon: <PiArrowLineDown />, label: 'sit' },
  { key: 'shadows', icon: <ShadowIcon />, label: 'shadows' },
  { key: 'occlusion', icon: <AmbientOcclusionIcon />, label: 'occlusion' },
  { key: 'grid', icon: <PiGridFour />, label: 'gridToggle' },
  { key: 'underLight', icon: <UnderLightIcon />, label: 'underLight' },
  { key: 'platform', icon: <PlatformIcon />, label: 'platform' },
];

export interface EnvironmentBarProps {
  environment: Environment;
  onToggle: (key: EnvironmentToggle) => void;
  underLight: UnderLightSettings;
  onUnderLightChange: (settings: UnderLightSettings) => void;
  backgroundOn: boolean;
  onBackgroundFile: (file: File) => void;
  onBackgroundRemove: () => void;
  /** Why the last picture could not be used, until the next one is picked. */
  backgroundError?: string;
  onFitView: () => void;
}

const EnvironmentBar: React.FC<EnvironmentBarProps> = ({
  environment,
  onToggle,
  underLight,
  onUnderLightChange,
  backgroundOn,
  onBackgroundFile,
  onBackgroundRemove,
  backgroundError,
  onFitView,
}) => {
  const intl = useIntl();
  const portal = usePortalContainer();
  const { narrow } = useChromeLayout();
  const panelRef = useRef<HTMLDivElement>(null);
  const swatchRef = useRef<HTMLButtonElement>(null);
  const lightPanel = usePopover(swatchRef, panelRef);
  const fileRef = useRef<HTMLInputElement>(null);
  const settingsLabel = intl.formatMessage(messages.underLightSettings);
  const backgroundLabel = intl.formatMessage(backgroundOn ? messages.backgroundRemove : messages.backgroundLoad);

  const toggle = ({ key, icon, label }: (typeof TOGGLES)[number]) => (
    <ChromeButton
      key={key}
      label={intl.formatMessage(messages[label])}
      icon={icon}
      pressed={environment[key]}
      tooltipPlacement={TOOLTIP_PLACEMENT_TOP}
      onClick={() => {
        if (key === 'underLight' && environment.underLight) lightPanel.close();
        onToggle(key);
      }}
      testId={`model-viewer__env-${key}`}
    />
  );

  return (
    <BottomSlot>
      {backgroundError && <Notice role="alert">{backgroundError}</Notice>}
      <Toolbar
        variant={TOOLBAR_VARIANT_FLOATING}
        tone={TOOLBAR_TONE_SURFACE}
        density={TOOLBAR_DENSITY_REGULAR}
        ariaLabel={intl.formatMessage(messages.environment)}
        dataTestId="model-viewer__environment"
      >
        {TOGGLES.map((entry) =>
          entry.key === 'underLight' ? (
            <Toolbar.Group key={entry.key}>
              <LightGroup>
                {toggle(entry)}
                {environment.underLight && (
                  <Tooltip
                    content={settingsLabel}
                    placement={TOOLTIP_PLACEMENT_TOP}
                    open={lightPanel.tooltipOpen}
                    portal
                    portalContainer={portal}
                  >
                    <Swatch
                      ref={swatchRef}
                      type="button"
                      aria-label={settingsLabel}
                      aria-expanded={lightPanel.open}
                      $color={underLight.color}
                      $narrow={narrow}
                      onClick={lightPanel.toggle}
                      data-testid="model-viewer__under-light-swatch"
                    />
                  </Tooltip>
                )}
                {environment.underLight && lightPanel.open && (
                  <UnderLightPanel ref={panelRef} settings={underLight} onChange={onUnderLightChange} />
                )}
              </LightGroup>
            </Toolbar.Group>
          ) : (
            <Toolbar.Group key={entry.key}>{toggle(entry)}</Toolbar.Group>
          ),
        )}
        <Toolbar.Group>
          <ChromeButton
            label={backgroundLabel}
            icon={<PiImage />}
            pressed={backgroundOn}
            tooltipPlacement={TOOLTIP_PLACEMENT_TOP}
            onClick={() => (backgroundOn ? onBackgroundRemove() : fileRef.current?.click())}
            testId="model-viewer__env-background"
          />
          <HiddenFileInput
            ref={fileRef}
            type="file"
            accept={BACKDROP_TYPES.join(',')}
            tabIndex={-1}
            aria-hidden="true"
            data-testid="model-viewer__background-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) onBackgroundFile(file);
            }}
          />
        </Toolbar.Group>
        <Toolbar.Group>
          <ChromeButton
            label={intl.formatMessage(messages.fitView)}
            icon={<PiCornersOut />}
            tooltipPlacement={TOOLTIP_PLACEMENT_TOP}
            onClick={onFitView}
            testId="model-viewer__fit-view"
          />
        </Toolbar.Group>
      </Toolbar>
    </BottomSlot>
  );
};

export default EnvironmentBar;
